/**
 * Exact fixed-point decimal arithmetic on `bigint`.
 *
 * The server's contract (invariant 5 in CLAUDE.md, D-15 in PROJECT.md) is:
 *
 *   * quantities          `Numeric(20, 4)`
 *   * per-unit rates      `Numeric(20, 4)`
 *   * extended amounts    integer paise, `BigInteger`
 *
 * None of those survive a round trip through a JavaScript `number`. `0.1 + 0.2` is the
 * cheap illustration; the expensive one is that a `BigInteger` paise column can hold a
 * value past 2^53, and `JSON.parse` silently truncates it. Pydantic serialises `Decimal`
 * as a JSON *string*, so the exact digits arrive on the wire — this module's whole job is
 * to keep them exact from there to the screen.
 *
 * Rounding is ROUND_HALF_UP — half away from zero — everywhere, matching
 * `core/rounding.py`. There is deliberately no other mode: Inai Accounts rounds half-even
 * only because nothing in its backend sets a decimal context, and that divergence is
 * already documented as intentional on the Python side.
 */
const DECIMAL_PATTERN = /^([+-]?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;
/** Cache of 10^n, because `10n ** 20n` in a render loop is measurable. */
const POW10 = [1n];
function pow10(n) {
    if (n < 0)
        throw new RangeError(`pow10 requires a non-negative exponent, got ${n}`);
    for (let i = POW10.length; i <= n; i += 1) {
        // `POW10[i - 1]` is always present: the loop only ever extends by one.
        POW10.push(POW10[i - 1] * 10n);
    }
    return POW10[n];
}
export class DecimalParseError extends Error {
    input;
    constructor(input) {
        super(`not a decimal value: ${JSON.stringify(input)}`);
        this.input = input;
        this.name = 'DecimalParseError';
    }
}
/**
 * A decimal held as a scaled integer: `units / 10^scale`, exactly.
 *
 * Immutable. Every operation returns a new instance, and no operation is lossy except
 * `round`, which is lossy on purpose and says so in its name.
 */
export class Dec {
    units;
    scale;
    constructor(
    /** The unscaled integer. Signed. */
    units, 
    /** Number of decimal places. Always >= 0. */
    scale) {
        this.units = units;
        this.scale = scale;
    }
    static ZERO = new Dec(0n, 0);
    static ONE = new Dec(1n, 0);
    static fromUnits(units, scale) {
        if (!Number.isInteger(scale) || scale < 0) {
            throw new RangeError(`scale must be a non-negative integer, got ${scale}`);
        }
        return new Dec(units, scale);
    }
    /**
     * Parse a decimal. Accepts a string (the wire format), a bigint, or another Dec.
     *
     * `number` is not accepted, and that is the point — see `Dec.unsafeFromNumber` for the
     * escape hatch and the warning that comes with it.
     */
    static parse(input) {
        if (input instanceof Dec)
            return input;
        if (typeof input === 'bigint')
            return new Dec(input, 0);
        const match = DECIMAL_PATTERN.exec(input.trim());
        if (!match)
            throw new DecimalParseError(input);
        const [, sign = '', whole = '0', fraction = '', exponent] = match;
        let units = BigInt(whole + fraction);
        let scale = fraction.length;
        if (exponent) {
            scale -= Number.parseInt(exponent, 10);
            if (scale < 0) {
                units *= pow10(-scale);
                scale = 0;
            }
        }
        return new Dec(sign === '-' ? -units : units, scale);
    }
    /**
     * Read a decimal out of a JSON value that may be a string or a number.
     *
     * FastAPI serialises every `Decimal` as a string, so the number branch should never
     * fire against this module's own API. It exists because a host may proxy the response
     * through something that re-encodes it, and silently producing a wrong number there
     * would be worse than the small risk taken here: a JSON number is routed through
     * `String(value)`, which yields the shortest decimal that round-trips, so `18.5` reads
     * back as exactly `18.5`. What it cannot recover is precision the number never had.
     */
    static fromJson(value) {
        if (typeof value === 'number') {
            if (!Number.isFinite(value))
                throw new DecimalParseError(value);
            return Dec.parse(String(value));
        }
        return Dec.parse(value);
    }
    /**
     * Deliberately awkward name. A `number` has already lost whatever precision it was
     * going to lose before this function sees it; use it for a slider or a chart axis,
     * never for a quantity that will be posted or an amount that will be shown.
     */
    static unsafeFromNumber(value) {
        if (!Number.isFinite(value))
            throw new DecimalParseError(value);
        return Dec.parse(String(value));
    }
    // ------------------------------------------------------------------ arithmetic
    static align(a, b) {
        if (a.scale === b.scale)
            return [a.units, b.units, a.scale];
        const scale = Math.max(a.scale, b.scale);
        return [a.units * pow10(scale - a.scale), b.units * pow10(scale - b.scale), scale];
    }
    add(other) {
        const [x, y, scale] = Dec.align(this, Dec.parse(other));
        return new Dec(x + y, scale);
    }
    sub(other) {
        const [x, y, scale] = Dec.align(this, Dec.parse(other));
        return new Dec(x - y, scale);
    }
    /**
     * Exact product. The scale is the sum of the operand scales and nothing is rounded.
     *
     * That matters: `core/rounding.py::to_paise` requires the FULL-precision product,
     * because rounding a rate and then multiplying rounds twice and drifts. Keeping `mul`
     * lossless is what lets `extendedAmount` round exactly once.
     */
    mul(other) {
        const rhs = Dec.parse(other);
        return new Dec(this.units * rhs.units, this.scale + rhs.scale);
    }
    neg() {
        return new Dec(-this.units, this.scale);
    }
    abs() {
        return this.units < 0n ? this.neg() : this;
    }
    /** Round to `places` decimals, half away from zero. The one lossy operation. */
    round(places) {
        if (!Number.isInteger(places) || places < 0) {
            throw new RangeError(`places must be a non-negative integer, got ${places}`);
        }
        if (places >= this.scale) {
            return new Dec(this.units * pow10(places - this.scale), places);
        }
        const divisor = pow10(this.scale - places);
        const quotient = this.units / divisor; // bigint division truncates toward zero
        const remainder = this.units % divisor;
        const doubled = (remainder < 0n ? -remainder : remainder) * 2n;
        if (doubled >= divisor) {
            return new Dec(quotient + (this.units < 0n ? -1n : 1n), places);
        }
        return new Dec(quotient, places);
    }
    // ------------------------------------------------------------------ comparison
    cmp(other) {
        const [x, y] = Dec.align(this, Dec.parse(other));
        if (x < y)
            return -1;
        if (x > y)
            return 1;
        return 0;
    }
    eq(other) {
        return this.cmp(other) === 0;
    }
    lt(other) {
        return this.cmp(other) < 0;
    }
    lte(other) {
        return this.cmp(other) <= 0;
    }
    gt(other) {
        return this.cmp(other) > 0;
    }
    gte(other) {
        return this.cmp(other) >= 0;
    }
    get isZero() {
        return this.units === 0n;
    }
    get isNegative() {
        return this.units < 0n;
    }
    get isPositive() {
        return this.units > 0n;
    }
    // ---------------------------------------------------------------- presentation
    /** The exact digits, with no grouping and no trailing-zero trimming. */
    toString() {
        if (this.scale === 0)
            return this.units.toString();
        const negative = this.units < 0n;
        const digits = (negative ? -this.units : this.units).toString().padStart(this.scale + 1, '0');
        const cut = digits.length - this.scale;
        return `${negative ? '-' : ''}${digits.slice(0, cut)}.${digits.slice(cut)}`;
    }
    /** Round to `places` and render with exactly that many decimals. */
    toFixed(places) {
        return this.round(places).toString();
    }
    /** Drop trailing zeros without changing the value. `1.2300` -> `1.23`, `5.0000` -> `5`. */
    trim() {
        let { units, scale } = this;
        while (scale > 0 && units % 10n === 0n) {
            units /= 10n;
            scale -= 1;
        }
        return new Dec(units, scale);
    }
    /** So `JSON.stringify` on a payload built from Dec values emits the wire format. */
    toJSON() {
        return this.toString();
    }
    /**
     * Lossy by definition. Present for charting and for `aria-valuenow`; never call it on
     * the way into a request body.
     */
    toUnsafeNumber() {
        return Number(this.toString());
    }
}
/** Shorthand. `dec('12.3456')` reads better in a component than `Dec.parse(...)`. */
export function dec(input) {
    return Dec.parse(input);
}
/** Sum a list exactly. Empty list is zero, not NaN. */
export function sumDec(values) {
    return values.reduce((acc, v) => acc.add(v), Dec.ZERO);
}
/** Decimal places the server keeps for a quantity or a per-unit rate: `Numeric(20, 4)`. */
export const RATE_PLACES = 4;
export const QUANTITY_PLACES = 4;
//# sourceMappingURL=decimal.js.map